import { TranslateService } from '@ngx-translate/core';
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as _ from 'lodash';

import { SupplierDto } from '../../api/tur-rc/models/supplier-dto';
import { AttachmentModel } from '../../content/recourse-booking/model/attachment.model';
import { RecourseBookingModel } from '../../content/recourse-booking/model/recourse-booking-model';
import { UploadFileModel } from '../../content/recourse-booking/model/upload-file-model';
import { BookingStateEnum } from '../../shared/enums/booking-state-enum';
import { NetworkActionEnum } from '../../shared/enums/network-action-enum';
import { RecourseTypeIdEnum } from '../../shared/enums/recourse-type-id-enum';
import { NotificationServiceWrapper } from '../notification-service-wrapper/notification-service-wrapper';
import { User } from '../user-service/model/user.model';
import { UserRights } from '../user-service/user-rights.enum';

export function isEditable(recourseBookingModel: RecourseBookingModel, user: User, withoutUserCheck: boolean): boolean {
  const manualBooking = recourseBookingModel.manualBooking;
  const bookingState = recourseBookingModel.bookingStateId;
  const recourseTypeId = recourseBookingModel.recourseTypeId;
  const useridCreate = recourseBookingModel.useridCreate;
  const rights = user.rights;
  const ppnUserId = user.ppnUserId;
  return (
    checkConditions(
      manualBooking,
      useridCreate,
      rights,
      recourseTypeId,
      bookingState,
      ppnUserId,
      withoutUserCheck,
      false,
    ) && !isManipulated(recourseBookingModel)
  );
}

export function isManipulated(recourseBookingModel: RecourseBookingModel): boolean {
  const manipulated = !recourseBookingModel.signatureValid;
  return manipulated;
}

export function isViewable(recourseBookingModel: RecourseBookingModel, user: User): boolean {
  const recourseTypeId: number = recourseBookingModel.recourseTypeId;
  const rights = user.rights;
  return checkReadRightsForRecourseType(rights, recourseTypeId);
}

export function checkReadRightsForRecourseType(rights: UserRights[], recourseTypeId: number): boolean {
  return (
    checkUserRightForRecourseType(rights, UserRights.RECOURSE_READ, recourseTypeId, RecourseTypeIdEnum.RECOURSE) ||
    checkUserRightForRecourseType(rights, UserRights.CAMPAIGNS_READ, recourseTypeId, RecourseTypeIdEnum.CAMPAIGN) ||
    checkUserRightForRecourseType(rights, UserRights.RAD_READ, recourseTypeId, RecourseTypeIdEnum.RAD) ||
    checkUserRightForRecourseType(rights, UserRights.AFTERSALES_READ, recourseTypeId, RecourseTypeIdEnum.AFTERSALES)
  );
}

export function checkBookingRightsForRecourseType(rights: UserRights[], recourseTypeId: number): boolean {
  return (
    checkUserRightForRecourseType(rights, UserRights.RECOURSE_BOOKING, recourseTypeId, RecourseTypeIdEnum.RECOURSE) ||
    checkUserRightForRecourseType(rights, UserRights.CAMPAIGNS_BOOKING, recourseTypeId, RecourseTypeIdEnum.CAMPAIGN) ||
    checkUserRightForRecourseType(rights, UserRights.RAD_BOOKING, recourseTypeId, RecourseTypeIdEnum.RAD) ||
    checkUserRightForRecourseType(rights, UserRights.AFTERSALES_BOOKING, recourseTypeId, RecourseTypeIdEnum.AFTERSALES)
  );
}

export function checkReadRights(rights: UserRights[]): boolean {
  return (
    checkUserRight(rights, UserRights.RECOURSE_READ) ||
    checkUserRight(rights, UserRights.CAMPAIGNS_READ) ||
    checkUserRight(rights, UserRights.RAD_READ) ||
    checkUserRight(rights, UserRights.AFTERSALES_READ)
  );
}

export function checkBookingRights(rights: UserRights[]): boolean {
  return (
    checkUserRight(rights, UserRights.RECOURSE_BOOKING) ||
    checkUserRight(rights, UserRights.CAMPAIGNS_BOOKING) ||
    checkUserRight(rights, UserRights.RAD_BOOKING) ||
    checkUserRight(rights, UserRights.AFTERSALES_BOOKING)
  );
}

export function checkAftersalesApproveRights(rights: UserRights[]): boolean {
  return checkUserRight(rights, UserRights.AFTERSALES_APPROVE);
}

export function checkRecourseApproveRights(rights: UserRights[]): boolean {
  return checkUserRight(rights, UserRights.RECOURSE_APPROVE);
}

export function checkApproveRightsForRecourseType(rights: UserRights[], recourseTypeId: number): boolean {
  return (
    checkUserRightForRecourseType(rights, UserRights.RECOURSE_APPROVE, recourseTypeId, RecourseTypeIdEnum.RECOURSE) ||
    checkUserRightForRecourseType(rights, UserRights.CAMPAIGNS_APPROVE, recourseTypeId, RecourseTypeIdEnum.CAMPAIGN) ||
    checkUserRightForRecourseType(rights, UserRights.RAD_APPROVE, recourseTypeId, RecourseTypeIdEnum.RAD) ||
    checkUserRightForRecourseType(rights, UserRights.AFTERSALES_APPROVE, recourseTypeId, RecourseTypeIdEnum.AFTERSALES)
  );
}

export function canUserApproveBooking(user: User, booking: RecourseBookingModel): boolean {
  return (
    booking.originatorId &&
    booking.originatorId !== user.ppnUserId &&
    checkApproveRightsForRecourseType(user.rights, booking.recourseTypeId)
  );
}

export function isCancelable(bookingDetails: RecourseBookingModel, user: User, detailDialog: boolean): boolean {
  const manualBooking = bookingDetails.manualBooking;
  const bookingState = bookingDetails.bookingStateId;
  const recourseTypeId = bookingDetails.recourseTypeId;
  const useridCreate = bookingDetails.useridCreate;
  const rights = user.rights;
  const ppnUserId = user.ppnUserId;
  return checkConditions(
    manualBooking,
    useridCreate,
    rights,
    recourseTypeId,
    bookingState,
    ppnUserId,
    true,
    detailDialog,
  );
}

export function isBookingCancelable(bookingDetails: RecourseBookingModel, user: User): boolean {
  const bookingState = bookingDetails.bookingStateId;
  const recourseTypeId = bookingDetails.recourseTypeId;
  const rights = user.rights;
  return checkBookingStateAndUserRightsForBookingCancel(rights, recourseTypeId, bookingState);
}

export function checkConditions(
  manualBooking: string,
  useridCreate: string,
  rights: UserRights[],
  recourseTypeId: number,
  bookingState: number,
  ppnUserId: string,
  withoutUserCheck: boolean,
  detailDialog: boolean,
): boolean {
  const manualBookingCondition: boolean = detailDialog
    ? !isManualBooking(manualBooking)
    : isManualBooking(manualBooking);
  const useridSameAsCreatorCondition: boolean = checkBookingCreatorSameAsCurrentUser(ppnUserId, useridCreate);
  const bookingStateAndUserRightsCondition = checkBookingStateAndUserRights(
    rights,
    recourseTypeId,
    bookingState,
    detailDialog,
  );
  if (withoutUserCheck) {
    return manualBookingCondition && bookingStateAndUserRightsCondition;
  }
  return manualBookingCondition && useridSameAsCreatorCondition && bookingStateAndUserRightsCondition;
}

export function checkBookingStateAndUserRights(
  rights: UserRights[],
  recourseTypeId: number,
  bookingState: number,
  detailDialog: boolean,
): boolean {
  const bookingStateCondition = detailDialog
    ? checkBookingStates([BookingStateEnum.BLOCKED, BookingStateEnum.FAILED], bookingState)
    : checkBookingStates(
        [
          BookingStateEnum.IN_PROGRESS,
          BookingStateEnum.IN_APPROVAL,
          BookingStateEnum.APPROVED,
          BookingStateEnum.BLOCKED,
          BookingStateEnum.FAILED,
          BookingStateEnum.REJECTED,
        ],
        bookingState,
      );
  const userRightsCondition: boolean = checkBookingRightsForRecourseType(rights, recourseTypeId);
  return bookingStateCondition && userRightsCondition;
}

export function checkBookingStateAndUserRightsForBookingCancel(
  rights: UserRights[],
  recourseTypeId: number,
  bookingState: number,
): boolean {
  const bookingStateCondition = !checkBookingStates(
    [BookingStateEnum.CANCELLED, BookingStateEnum.BOOKED],
    bookingState,
  );
  const userRightsCondition: boolean = checkBookingRightsForRecourseType(rights, recourseTypeId);
  return bookingStateCondition && userRightsCondition;
}

export function checkUserRightForRecourseType(
  rights: UserRights[],
  userRight: UserRights,
  recourseTypeId: RecourseTypeIdEnum,
  recourseType: RecourseTypeIdEnum,
): boolean {
  return rights.includes(userRight) && recourseTypeId === recourseType;
}

export function checkUserRight(rights: UserRights[], userRight: UserRights): boolean {
  return rights.includes(userRight);
}

export function isManualBooking(manualBooking: string): boolean {
  let manualBookingCondition = false;
  if (!_.isNil(manualBooking) && manualBooking === '1') {
    manualBookingCondition = true;
  }
  return manualBookingCondition;
}

export function checkBookingStates(validBookingStates: BookingStateEnum[], bookingState: number): boolean {
  return validBookingStates.includes(bookingState);
}

export function checkBookingCreatorSameAsCurrentUser(ppnUserId: string, useridCreate: string): boolean {
  return ppnUserId === useridCreate;
}

export function newFormDataForAttachment(
  uploadFileModel: UploadFileModel,
  topic: string,
  recourseBookingIds: any[],
  recourseId: string,
  recourseExId: string,
): FormData {
  const formData = new FormData();
  formData.append('file', uploadFileModel.fileToUpload);
  formData.append('filename', uploadFileModel.filename);
  formData.append('external', uploadFileModel.extern.toString());
  formData.append('topic', topic);
  formData.append('recourseBookingIds', recourseBookingIds.toString());
  formData.append('recourseId', recourseId);
  formData.append('recourseExId', recourseExId);
  return formData;
}

export function* indexGenerator(): any {
  let index = 0;

  while (true) {
    yield index++;
  }
}

export function getErrorCodeKey(errorCode: number, action: NetworkActionEnum): string {
  if (errorCode) {
    return `ERROR_CODE_${errorCode}`;
  }
  switch (action.valueOf()) {
    case 0: {
      return 'ERROR_CODE_7100';
    }
    case 1: {
      return 'ERROR_CODE_7005';
    }
    case 2: {
      return 'ERROR_CODE_7008';
    }
    default: {
      return 'ERROR_CODE_0000';
    }
  }
}

export function getNetworkActionEnum(errorCode: number): NetworkActionEnum {
  switch (errorCode) {
    case 7100: {
      return NetworkActionEnum.Upload;
    }
    case 7005: {
      return NetworkActionEnum.Download;
    }
    case 7008: {
      return NetworkActionEnum.Delete;
    }
    default: {
      return NetworkActionEnum.Unknown;
    }
  }
}

export function getNetworkActionTitleKey(action: NetworkActionEnum): string {
  switch (action.valueOf()) {
    case 0: {
      return 'UPLOAD_FAILED_TITLE';
    }
    case 1: {
      return 'DOWNLOAD_FAILED_TITLE';
    }
    case 2: {
      return 'DELETE_FILE_FAILED_TITLE';
    }
    case 3: {
      return 'AUTHENTICITY_FILE_FAILED_TITLE';
    }
    default: {
      return 'NETWORK_ERROR_TITLE';
    }
  }
}

export function showAttachmentDownloadFailed(
  notificationServiceWrapper: NotificationServiceWrapper,
  translateService: TranslateService,
  attachmentFileName: any,
): void {
  translateService
    .get(['TITLE_FAILED', 'DOWNLOAD_ATTACHMENT_FAILED'])
    .subscribe((translate: { [key: string]: any }) => {
      const title: string = translate.TITLE_FAILED;
      const content: string = translateService.instant('DOWNLOAD_ATTACHMENT_FAILED', { 0: attachmentFileName });
      notificationServiceWrapper.addErrorNotification(title, content);
    });
}

export function showAttachmentUploadFailed(
  notificationServiceWrapper: NotificationServiceWrapper,
  translateService: TranslateService,
  attachmentFileName: any,
  errorResponse: any,
): void {
  translateService.get(['TITLE_FAILED', 'UPLOAD_ATTACHMENT_FAILED']).subscribe((translate: { [key: string]: any }) => {
    const title: string = translate.TITLE_FAILED;
    let content: string = translateService.instant('UPLOAD_ATTACHMENT_FAILED', { 0: attachmentFileName });

    if (errorResponse && errorResponse.error && errorResponse.error.errorCode === 7002) {
      content += translateService.instant('ERROR_CODE_7002');
    }

    notificationServiceWrapper.addErrorNotification(title, content);
  });
}

export function showAttachmentUploadSuccess(
  notificationServiceWrapper: NotificationServiceWrapper,
  translateService: TranslateService,
  attachmentFileName: any,
): void {
  translateService
    .get(['TITLE_UPLOAD_ATTACHMENT', 'UPLOAD_ATTACHMENT_SUCCESSFUL'])
    .subscribe((translate: { [key: string]: any }) => {
      const title: string = translate.TITLE_UPLOAD_ATTACHMENT;
      const content: string = translateService.instant('UPLOAD_ATTACHMENT_SUCCESSFUL', { 0: attachmentFileName });

      notificationServiceWrapper.addSuccessNotification(title, content);
    });
}

export function showAttachmentDeleteFailed(
  notificationServiceWrapper: NotificationServiceWrapper,
  translateService: TranslateService,
  attachmentFileName: any,
): void {
  translateService.get(['TITLE_FAILED', 'DELETE_ATTACHMENT_FAILED']).subscribe((translate: { [key: string]: any }) => {
    const title: string = translate.TITLE_FAILED;
    const content: string = translateService.instant('DELETE_ATTACHMENT_FAILED', { 0: attachmentFileName });

    notificationServiceWrapper.addErrorNotification(title, content);
  });
}

export function showAttachmentDeleteSuccess(
  notificationServiceWrapper: NotificationServiceWrapper,
  translateService: TranslateService,
  attachmentFileName: any,
): void {
  translateService
    .get(['TITLE_DELETE_ATTACHMENT', 'DELETE_ATTACHMENT_SUCCESSFUL'])
    .subscribe((translate: { [key: string]: any }) => {
      const title: string = translate.TITLE_DELETE_ATTACHMENT;
      const content: string = translateService.instant('DELETE_ATTACHMENT_SUCCESSFUL', { 0: attachmentFileName });

      notificationServiceWrapper.addSuccessNotification(title, content);
    });
}

export function showNetworkTransferErrorMessage(
  notificationServiceWrapper: NotificationServiceWrapper,
  translateService: TranslateService,
  response: any,
  action: NetworkActionEnum,
): void {
  translateService
    .get([
      'UPLOAD_FAILED_TITLE',
      'DOWNLOAD_FAILED_TITLE',
      'DELETE_FILE_FAILED_TITLE',
      'AUTHENTICITY_FILE_FAILED_TITLE',
      'AUTHENTICITY_RECOURSE_BOOKING_FAILED_TITLE',
      'AUTHENTICITY_RECOURSE_BOOKING_ATTACHMENTS_FAILED_TITLE',
      'NETWORK_ERROR_TITLE',
    ])
    .subscribe(() => {
      const title: string = translateService.instant(getNetworkActionTitleKey(action));
      const content: string = translateService.instant(getErrorCodeKey(response.error.errorCode, action));
      notificationServiceWrapper.addErrorNotification(title, content);
    });
}

export function handleVerification(
  notificationServiceWrapper: NotificationServiceWrapper,
  translateService: TranslateService,
  attachment: AttachmentModel,
): void {
  if (attachment) {
    translateService
      .get([
        'AUTHENTICITY_FILE_FAILED_TITLE',
        'AUTHENTICITY_FILE_CHECKSUM_FAILED_TITLE',
        'ERROR_CODE_8002',
        'ERROR_CODE_8003',
      ])
      .subscribe((translate: { [key: string]: any }) => {
        let title: string;
        let content: string;
        if (!attachment.checksumValid) {
          title = translate.AUTHENTICITY_FILE_CHECKSUM_FAILED_TITLE;
          content = translateService.instant('ERROR_CODE_8002', { 0: attachment.id, 1: attachment.filename });
          notificationServiceWrapper.addErrorNotification(title, content);
        }
        if (!attachment.signatureValid) {
          title = translate.AUTHENTICITY_FILE_FAILED_TITLE;
          content = translateService.instant('ERROR_CODE_8003', { 0: attachment.id, 1: attachment.filename });
          notificationServiceWrapper.addErrorNotification(title, content);
        }
      });
  }
}

export function translateBookingState(bookingStateId: number, translateService: TranslateService): string {
  let bookingStateName = '';
  switch (bookingStateId) {
    case BookingStateEnum.IN_PROGRESS: {
      bookingStateName = translateService.instant(BookingStateEnum[BookingStateEnum.IN_PROGRESS]);
      break;
    }
    case BookingStateEnum.IN_APPROVAL: {
      bookingStateName = translateService.instant(BookingStateEnum[BookingStateEnum.IN_APPROVAL]);
      break;
    }
    case BookingStateEnum.APPROVED: {
      bookingStateName = translateService.instant(BookingStateEnum[BookingStateEnum.APPROVED]);
      break;
    }
    case BookingStateEnum.BLOCKED: {
      bookingStateName = translateService.instant(BookingStateEnum[BookingStateEnum.BLOCKED]);
      break;
    }
    case BookingStateEnum.CANCELLED: {
      bookingStateName = translateService.instant(BookingStateEnum[BookingStateEnum.CANCELLED]);
      break;
    }
    case BookingStateEnum.BOOKED: {
      bookingStateName = translateService.instant(BookingStateEnum[BookingStateEnum.BOOKED]);
      break;
    }
    case BookingStateEnum.FAILED: {
      bookingStateName = translateService.instant(BookingStateEnum[BookingStateEnum.FAILED]);
      break;
    }
    case BookingStateEnum.REJECTED: {
      bookingStateName = translateService.instant(BookingStateEnum[BookingStateEnum.REJECTED]);
      break;
    }
    default: {
      break;
    }
  }
  return bookingStateName;
}

export function isCustomerCurrencyEuro(supplier: SupplierDto): boolean {
  if (supplier) {
    return supplier.currency.trim() === 'EUR';
  }
  return false;
}
